import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const TAAK_BRON = [
  'VERLOF',
  'VERZUIM',
  'DECLARATIE',
  'WEEKSTAAT',
  'CERTIFICAAT',
  'ONBOARDING',
  'MUTATIEVERZOEK',
  'WERKBON',
  'HANDMATIG',
  // Fase 2 (gap-fix) — gespiegeld met Prisma-enum TaakBron. KWALITEIT-bron
  // wordt door fase 6 (kwaliteit-module) gebruikt voor auto-gegenereerde
  // bevinding-taken; FORMULIER-bron door fase 7 (formulieren-module) bij
  // ingediende formulier-inzendingen die handmatige opvolging vereisen.
  'KWALITEIT',
  'FORMULIER',
  // Slimme signalering (spec 2026-05-26) — taken aangemaakt vanuit een
  // operationeel signaal via /notification-events/:id/taak.
  'SIGNAAL',
  // FO-035 (batch-16) — "gespreksverslag maken" voor de gespreksvoerder bij het
  // plannen van een gesprek met verslagVerplicht.
  'GESPREKSVERSLAG',
] as const
export type TaakBron = (typeof TAAK_BRON)[number]

export const TAAK_PRIORITEIT = ['LAAG', 'MIDDEL', 'HOOG', 'URGENT'] as const
export type TaakPrioriteit = (typeof TAAK_PRIORITEIT)[number]

export const TAAK_STATUS = ['OPEN', 'IN_BEHANDELING', 'AFGEROND', 'VERVALLEN'] as const
export type TaakStatus = (typeof TAAK_STATUS)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const TAAK_BRON_LABELS: Record<TaakBron, string> = {
  VERLOF:         'Verlof',
  VERZUIM:        'Verzuim',
  DECLARATIE:     'Declaratie',
  WEEKSTAAT:      'Weekstaat',
  CERTIFICAAT:    'Certificaat',
  ONBOARDING:     'Onboarding',
  MUTATIEVERZOEK: 'Mutatieverzoek',
  WERKBON:        'Werkbon',
  HANDMATIG:      'Handmatig',
  KWALITEIT:      'Kwaliteit',
  FORMULIER:      'Formulier',
  SIGNAAL:        'Signaal',
  GESPREKSVERSLAG: 'Gespreksverslag',
}

export const TAAK_PRIORITEIT_LABELS: Record<TaakPrioriteit, string> = {
  LAAG:   'Laag',
  MIDDEL: 'Middel',
  HOOG:   'Hoog',
  URGENT: 'Urgent',
}

export const TAAK_STATUS_LABELS: Record<TaakStatus, string> = {
  OPEN:           'Open',
  IN_BEHANDELING: 'In behandeling',
  AFGEROND:       'Afgerond',
  VERVALLEN:      'Vervallen',
}

// Badge-variant per prioriteit voor consistent kleurgebruik in cards/tabel.
export const TAAK_PRIORITEIT_BADGE_VARIANT: Record<
  TaakPrioriteit,
  'secondary' | 'info' | 'warning' | 'danger'
> = {
  LAAG:   'secondary',
  MIDDEL: 'info',
  HOOG:   'warning',
  URGENT: 'danger',
}

// Hogere waarde = belangrijker. Gebruikt voor sortering in lijst + dashboard-widget.
export const TAAK_PRIORITEIT_VOLGORDE: Record<TaakPrioriteit, number> = {
  URGENT: 4,
  HOOG:   3,
  MIDDEL: 2,
  LAAG:   1,
}

// ─── DTOs ───────────────────────────────────────────────────────────────────

export interface TaakDTO {
  id: string
  userId: string
  bron: TaakBron
  prioriteit: TaakPrioriteit
  status: TaakStatus
  titel: string
  inhoud: string
  link: string | null
  bronEntiteitId: string | null
  bronModule: string | null
  deadlineOp: string | null
  afgerondOp: string | null
  afgerondDoor: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Per-user aggregaat voor backoffice admin-overzicht.
 * Telt alleen status=OPEN of IN_BEHANDELING (niet afgerond/vervallen).
 */
export interface TaakStatsRowDTO {
  userId: string
  userName: string | null
  userEmail: string
  aantalOpen: number
  aantalUrgent: number
  aantalHoog: number
  oudsteOpenCreatedAt: string | null
}

export interface TaakStatsDTO {
  totaalOpen: number
  totaalUrgent: number
  perUser: TaakStatsRowDTO[]
}

// ─── Zod schemas ────────────────────────────────────────────────────────────

export const listTakenQuerySchema = z.object({
  status:     z.enum(TAAK_STATUS).optional(),
  prioriteit: z.enum(TAAK_PRIORITEIT).optional(),
  bron:       z.enum(TAAK_BRON).optional(),
  userId:     idParam().optional(),
  /** Vrij-tekst zoekterm op titel/inhoud (case-insensitive, server-side). */
  zoek:       z.string().trim().min(1).max(200).optional(),
  page:       z.coerce.number().int().positive().default(1),
  pageSize:   z.coerce.number().int().positive().max(100).default(50),
})

export type ListTakenQuery = z.infer<typeof listTakenQuerySchema>

export const taakStatsQuerySchema = z.object({
  limit: z.coerce.number().int().positive().max(200).default(50),
})

export type TaakStatsQuery = z.infer<typeof taakStatsQuerySchema>

/**
 * Body voor POST /taken/handmatig — admin/HR maakt een handmatige reminder
 * voor zichzelf of een andere user (bv. "bel klant X terug").
 *
 * `userId` optioneel: zonder veld is de eigenaar de ingelogde user. Met `userId`
 * (alleen door admins met canEdit) wordt de taak voor iemand anders aangemaakt.
 */
export const createHandmatigeTaakSchema = z.object({
  userId:     idParam().optional(),
  titel:      z.string().trim().min(1).max(200),
  inhoud:     z.string().trim().min(1).max(2000),
  link:       z.string().trim().max(500).nullable().optional(),
  prioriteit: z.enum(TAAK_PRIORITEIT).optional().default('MIDDEL'),
  deadlineOp: z.string().datetime().nullable().optional(),
})

export type CreateHandmatigeTaakInput = z.infer<typeof createHandmatigeTaakSchema>

/**
 * Body voor GET /taken/badge-aantal — combineert URGENT (alle status OPEN/IN_BEHANDELING)
 * met DEZE_WEEK (open taken met deadlineOp binnen 7 dagen).
 *
 * Wordt door de bell-badge in portal+backoffice topbar gepoll'd.
 */
export interface TaakBadgeAantalDTO {
  urgent: number
  dezeWeek: number
  totaal: number
}

/** PATCH /taken/:id — admin wijzigt status en/of prioriteit van een taak. */
export const taakUpdateSchema = z.object({
  status:     z.enum(TAAK_STATUS).optional(),
  prioriteit: z.enum(TAAK_PRIORITEIT).optional(),
})
export type TaakUpdateInput = z.infer<typeof taakUpdateSchema>

/** POST /taken/bulk-status — admin zet status op meerdere taken tegelijk. */
export const taakBulkStatusSchema = z.object({
  ids:    z.array(idParam()).min(1).max(100),
  status: z.enum(TAAK_STATUS),
})
export type TaakBulkStatusInput = z.infer<typeof taakBulkStatusSchema>
